import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getBaseLanguageNarratives,
  getTranslationNarratives,
  updateBaseNarrativeValue,
  updateNarrativeTranslationStatusBulk,
  updateBaseNarrativeStatus,
  getReportingPeriods,
  getFundsList,
} from 'Services/overviewApi';
import classes from './Narratives.module.scss';

const Narratives = () => {
  const context = useContext(AppContext);
  const gridRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [narratives, setNarratives] = useState([]);
  const [filteredNarratives, setFilteredNarratives] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFund, setSelectedFund] = useState('0 All Funds');
  const [fundOptions, setFundOptions] = useState([]);
  const [reportingPeriodOptions, setReportingPeriodOptions] = useState([]);

  const reportingPeriod = context.state?.selectedReportingDate?.code;
  const regulation = context.state?.selectedRegulation;
  const userEmail = context.state?.userEmail || 'Unknown';

  const formatDate = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const loadData = useCallback(async () => {
    if (!reportingPeriod || !regulation) {
      setLoading(false);
      setNarratives([]);
      setFilteredNarratives([]);
      return;
    }
    try {
      setLoading(true);
      const rpFormatted = typeof reportingPeriod === 'string'
        ? reportingPeriod
        : new Date(reportingPeriod).toISOString().split('T')[0];

      const [narrativesRes, fundsRes, periodsRes] = await Promise.all([
        getBaseLanguageNarratives(rpFormatted, regulation.Name),
        getFundsList(rpFormatted, regulation.Name, '', ''),
        getReportingPeriods(),
      ]);

      const narrativeData = narrativesRes.value || narrativesRes.Content || [];
      setNarratives(narrativeData);
      setFilteredNarratives(narrativeData);

      const fundsData = fundsRes.value || fundsRes.Content || [];
      const opts = fundsData.map((f) => ({
        label: `${f.fundname} (${f.fundcode})`,
        value: f.fundcode,
      }));
      opts.push({ label: 'All Funds', value: '0 All Funds' });
      setFundOptions(opts);

      const periodsData = periodsRes.value || periodsRes.Content || [];
      setReportingPeriodOptions(periodsData.map((rp) => ({
        label: formatDate(rp.Reporting_Period),
        value: rp.Reporting_Period,
      })));
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load narratives'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod, regulation, context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterByFund = useCallback((data, fundCode) => {
    if (!fundCode || fundCode === '0 All Funds') return data;
    return data.filter((n) => n.fundcode === fundCode);
  }, []);

  const handleFundChange = (e) => {
    const fund = e.target.value;
    setSelectedFund(fund);
    setSearchValue('');
    setFilteredNarratives(filterByFund(narratives, fund));
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setFilteredNarratives(filterByFund(narratives, selectedFund));
      return;
    }
    try {
      setLoading(true);
      setSelectedFund('0 All Funds');
      const rpFormatted = typeof reportingPeriod === 'string'
        ? reportingPeriod
        : new Date(reportingPeriod).toISOString().split('T')[0];

      const res = await getBaseLanguageNarratives(rpFormatted, regulation.Name);
      const data = res.value || res.Content || [];
      const searchClasses = searchValue.split(';').map((s) => s.trim()).filter(Boolean);
      const filtered = data.filter((n) => n.classcode && searchClasses.includes(n.classcode));
      setNarratives(data);
      setFilteredNarratives(filtered);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to search narratives'}</> });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const getReferenceLabel = (narrative) => {
    if (!narrative.fundcode && !narrative.classcode) return 'Template';
    if (!narrative.classcode) return `Fund (${narrative.fundcode})`;
    return `Share (${narrative.classcode})`;
  };

  const handleCellEditingStopped = async (event) => {
    const narrative = event.data;
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    if (oldValue === newValue || narrative.translationstatusid === 3) return;

    try {
      setLoading(true);
      await updateBaseNarrativeValue(narrative);

      const rpFormatted = typeof reportingPeriod === 'string'
        ? reportingPeriod
        : new Date(reportingPeriod).toISOString().split('T')[0];

      // Get translated narratives to cascade status update
      const transRes = await getTranslationNarratives(rpFormatted, regulation.Name);
      const translatedList = transRes.value || transRes.Content || [];
      const relatedTranslated = translatedList.filter((t) => t.basenarrativeid === narrative.narrativeid);
      const translatedIds = relatedTranslated.map((t) => t.targetnarrativeid);

      // Update translated narratives to "Needs Translation" (statusId = 2)
      if (translatedIds.length > 0) {
        await updateNarrativeTranslationStatusBulk(translatedIds, 2, userEmail);
      }

      // Update base narrative status to "Needs Approval" (statusId = 1)
      await updateBaseNarrativeStatus([narrative.narrativeid], 1, userEmail);

      // Reload data
      const refreshRes = await getBaseLanguageNarratives(rpFormatted, regulation.Name);
      const refreshedData = refreshRes.value || refreshRes.Content || [];
      setNarratives(refreshedData);

      if (searchValue.trim()) {
        const searchClasses = searchValue.split(';').map((s) => s.trim()).filter(Boolean);
        setFilteredNarratives(refreshedData.filter((n) => n.classcode && searchClasses.includes(n.classcode)));
      } else {
        setFilteredNarratives(filterByFund(refreshedData, selectedFund));
      }

      context.handlers.setToast({ type: 'success', body: <>Narrative has successfully changed.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to update narrative'}</> });
    } finally {
      setLoading(false);
    }
  };

  const isEditable = (params) => {
    const statusId = params.data?.translationstatusid;
    return statusId === 1 || statusId === 4;
  };

  const columnDefs = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'ID',
      field: 'narrativekey',
      width: 180,
      editable: false,
      cellStyle: { fontSize: '80%' },
    },
    {
      headerName: 'Reference',
      field: 'basenarrativeid',
      width: 180,
      editable: false,
      filter: false,
      cellRenderer: (params) => (
        <span style={{ fontSize: '80%' }}>
          <b>{getReferenceLabel(params.data)}</b>
        </span>
      ),
    },
    {
      headerName: 'Narrative',
      field: 'narrativevalue',
      flex: 1,
      editable: isEditable,
      filter: false,
      cellRenderer: (params) => (
        <span style={{ fontSize: '80%' }}>{params.value}</span>
      ),
    },
  ];

  const onSelectionChanged = () => {
    const selected = gridRef.current?.api?.getSelectedRows() || [];
    setSelectedRows(selected);
  };

  if (loading) {
    return <GrpSpinner />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <div className={classes.fundPicker}>
          <select
            value={selectedFund}
            onChange={handleFundChange}
            className={classes.select}
          >
            {fundOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className={classes.searchBox}>
          <input
            type="text"
            placeholder="Search Share Classes (semicolon-separated)"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className={classes.searchInput}
          />
        </div>
      </div>

      <div className={classes.sectionHeader}>
        <h3>Editing</h3>
      </div>

      <div className={`ag-theme-reborn ${classes.gridWrapper}`}>
        <AgGridReact
          ref={gridRef}
          rowData={filteredNarratives}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: true,
          }}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          onCellEditingStopped={handleCellEditingStopped}
          pagination={true}
          paginationPageSize={20}
          domLayout="autoHeight"
          stopEditingWhenCellsLoseFocus={true}
        />
      </div>
    </div>
  );
};

export default Narratives;
